import { useEffect, useContext, useMemo, useState } from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Paper, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { enqueueSnackbar } from 'notistack';
// import DottedMap from 'dotted-map/without-countries';
import { AuthContext } from '../context/AuthProvider';
import LoginForm from '../reusable-component/LoginForm';
import graphicImage from '../images/graphic.svg';
import lamp from '../images/lamp.svg';
import themeStyles from '../utils/theme';
import { MAIN } from '../utils/commonConstants';
import MyMapString from '../utils/myMap.json';
import { exchangeCognitoCode } from '../auth/cognitoToken';
import { AWS_CONFIG_TYPE } from '../BaseUrl';
import { getUserPermissions } from "../api's/ManagementApi";
import LoadingIcon from '../reusable-component/LoadingIcon';
import { playSuccessSound } from '../utils/playSuccessSound';

export default function Login() {
  const classes = themeStyles();
  const theme = 'nagarro';
  const muiTheme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const { setUser, setToken, setUserId, setUserGroup, setLLMId, setLLMName } = useContext(AuthContext);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const authError = params.get('error');

    if (authError) {
      enqueueSnackbar('SSO login failed. Please try again.', { variant: 'error' });
      window.history.replaceState({}, document.title, '/login');
      return;
    }

    if (!code) return;

    (async () => {
      try {
        setLoading(true);
        const tokens = await exchangeCognitoCode(code);
        const claims = jwtDecode(tokens.id_token);

        sessionStorage.setItem('jwtToken', JSON.stringify(tokens.access_token));
        sessionStorage.setItem('idToken', JSON.stringify(tokens.id_token));
        sessionStorage.setItem('refreshToken', JSON.stringify(tokens.refresh_token));
        sessionStorage.setItem('email', claims.email);
        sessionStorage.setItem('full_name', JSON.stringify(claims.name));
        sessionStorage.setItem('version', JSON.stringify('2.0'));
        sessionStorage.setItem('userGroup', JSON.stringify('Group1'));
        sessionStorage.setItem('user', JSON.stringify(claims.name));
        sessionStorage.setItem('LLMId', AWS_CONFIG_TYPE ? 'llm-2' : 'llm-1');
        sessionStorage.setItem('LLMName', AWS_CONFIG_TYPE ? 'Bedrock' : 'ChatGPT');

        try {
          const permissionsResponse = await getUserPermissions(claims.email);
          const userPermissions = permissionsResponse?.permissions || [];
          const userId = permissionsResponse?.userId || '';
          sessionStorage.setItem('userPermissions', JSON.stringify(userPermissions));
          sessionStorage.setItem('id', JSON.stringify(userId));
          playSuccessSound();
          enqueueSnackbar('Login successful!', { variant: 'success' });
          setUser(claims.name);
          setToken(tokens.id_token);
          setUserId(userId);
          setUserGroup('Group1');
          setLLMId('llm-2');
          setLLMName('Bedrock');
        } catch (permError) {
          console.error('Failed to fetch user permissions:', permError);
        }

        // Clean URL
        window.history.replaceState({}, document.title, '/login');

        navigate('/dashboard');
      } catch (err) {
        console.error('Cognito login failed:', err);
        enqueueSnackbar('Cognito login failed. Please try again.', { variant: 'error' });
      }
      setLoading(false);
    })();
  }, []);

  // Media queries for different breakpoints
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(muiTheme.breakpoints.between('sm', 'md'));
  const isLaptop = useMediaQuery(muiTheme.breakpoints.between('md', 'lg'));
  const isDesktop = useMediaQuery(muiTheme.breakpoints.between('lg', 'xl'));
  const isLargeScreen = useMediaQuery(muiTheme.breakpoints.up('xl'));

  // const svgMap = useMemo(() => {
  //   const map = new DottedMap({ map: MyMapString });

  //   map.addPin({ lat: 40.73061, lng: -73.935242, svgOptions: { color: '#d6ff79', radius: 0.4 } });
  //   map.addPin({ lat: 48.8534, lng: 2.3488, svgOptions: { color: '#fffcf2', radius: 0.4 } });
  //   map.addPin({ lat: 51.5074, lng: -0.1278, svgOptions: { color: '#ff6f61', radius: 0.4 } });
  //   map.addPin({ lat: 35.6895, lng: 139.6917, svgOptions: { color: '#6b5b95', radius: 0.4 } });
  //   map.addPin({ lat: -33.8688, lng: 151.2093, svgOptions: { color: '#88b04b', radius: 0.4 } });
  //   map.addPin({ lat: 55.7558, lng: 37.6173, svgOptions: { color: '#f7cac9', radius: 0.4 } });
  //   map.addPin({ lat: 34.0522, lng: -118.2437, svgOptions: { color: '#92a8d1', radius: 0.4 } });
  //   map.addPin({ lat: 19.4326, lng: -99.1332, svgOptions: { color: '#955251', radius: 0.4 } });
  //   map.addPin({ lat: -23.5505, lng: -46.6333, svgOptions: { color: '#b565a7', radius: 0.4 } });
  //   map.addPin({ lat: 39.9042, lng: 116.4074, svgOptions: { color: '#009b77', radius: 0.4 } });

  //   return map.getSVG({
  //     radius: 0.22,
  //     color: '#423B38',
  //     shape: 'circle',
  //     backgroundColor: '#020300',
  //   });
  // }, []);

  return (
    <Grid container style={{ minHeight: '100vh' }} className={classes[`${theme}ThemeColor`]}>
      {/* Left side with map */}
      <Grid
        item
        xs={12}
        md={7}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          minHeight: { xs: '40vh', sm: '50vh', md: '100vh' },
          display: { xs: 'flex', md: 'block' },
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        {/* <Box
          component="img"
          src={`data:image/svg+xml;utf8,${encodeURIComponent(svgMap)}`}
          alt="background map"
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: 0.3,
            top: 0,
            left: 0,
          }}
        /> */}

        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            marginLeft: { xs: '20px', sm: '30px', md: '35px' },
            marginTop: { xs: '20px', md: '0' },
          }}
        >
          <img
            src={lamp}
            alt="login"
            style={{
              marginLeft: isMobile ? '10px' : isTablet ? '20px' : '25px',
              width: isMobile ? '100px' : isTablet ? '130px' : isLaptop ? '160px' : isDesktop ? '180px' : '200px',
              height: isMobile ? '120px' : isTablet ? '150px' : isLaptop ? '180px' : isDesktop ? '200px' : '220px',
            }}
          />
          <Typography
            variant={isMobile ? 'h4' : isTablet ? 'h3' : 'h2'}
            gutterBottom
            component="h4"
            sx={{
              marginTop: '10px',
              color: '#FFFFFF',
              textAlign: 'left',
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem', lg: '3rem', xl: '3.5rem' },
              paddingRight: { xs: '20px', md: '0' },
            }}
          >
            {MAIN.xperience360}
          </Typography>
        </Box>

        {/* Graphic image - repositioned for mobile */}
        <Box
          sx={{
            position: { xs: 'relative', md: 'absolute' },
            bottom: { xs: 'auto', md: '20%' },
            left: { xs: 'auto', md: '35%' },
            right: { xs: '0', md: 'auto' },
            zIndex: 1,
            display: 'flex',
            justifyContent: { xs: 'center', md: 'flex-start' },
            marginTop: { xs: '20px', md: '0' },
            marginBottom: { xs: '20px', md: '0' },
            transform: {
              xs: 'none',
              md: 'translateX(0)',
              lg: 'translateX(5%)',
              xl: 'translateX(10%)',
            },
          }}
        >
          <img
            src={graphicImage}
            alt="login"
            style={{
              width: isMobile ? '250px' : isTablet ? '350px' : isLaptop ? '450px' : isDesktop ? '550px' : '650px',
              height: isMobile ? '170px' : isTablet ? '230px' : isLaptop ? '300px' : isDesktop ? '370px' : '430px',
              maxWidth: '90%',
            }}
          />
        </Box>

        {/* Feature Badges - repositioned for mobile */}
        <Box
          sx={{
            position: { xs: 'relative', md: 'absolute' },
            bottom: { xs: 'auto', md: '7%' },
            left: { xs: 'auto', md: '10%' },
            zIndex: 1,
            marginBottom: { xs: '20px', md: '0' },
            paddingX: { xs: '20px', md: '0' },
          }}
        >
          <Box
            className="feature-badges"
            sx={{
              mt: { xs: 2, md: 4 },
              display: 'flex',
              gap: 1,
              flexWrap: 'nowrap',
              justifyContent: { xs: 'flex-start', md: 'flex-start' },
              overflowX: { xs: 'auto', md: 'visible' },
            }}
          >
            <Box
              className="badge"
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                padding: { xs: '4px 8px', md: '6px 12px' },
                whiteSpace: 'nowrap',
              }}
            >
              🔄 Real-time ETL
            </Box>
            <Box
              className="badge"
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                padding: { xs: '4px 8px', md: '6px 12px' },
                whiteSpace: 'nowrap',
              }}
            >
              🤖 AI-Driven Insights
            </Box>
            <Box
              className="badge"
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                padding: { xs: '4px 8px', md: '6px 12px' },
                whiteSpace: 'nowrap',
              }}
            >
              ⚡ Auto-Scaling
            </Box>
            <Box
              className="badge"
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                padding: { xs: '4px 8px', md: '6px 12px' },
                whiteSpace: 'nowrap',
              }}
            >
              🔒 Secure Pipeline
            </Box>
          </Box>
        </Box>
      </Grid>

      {/* Right side login form */}
      <Grid
        item
        xs={12}
        md={5}
        component={Paper}
        elevation={6}
        square
        sx={{
          borderTopLeftRadius: { xs: '40px', sm: '60px', md: '80px' },
          borderTopRightRadius: { xs: '40px', md: '0' },
          minHeight: { xs: 'auto', md: '100vh' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            my: { xs: 3, sm: 4, md: 5 },
            mx: { xs: 3, sm: 6, md: 12, lg: 18 },
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            maxWidth: { xs: '100%', sm: '400px', md: '100%' },
          }}
        >
          <LoginForm />
        </Box>
      </Grid>
      {loading && <LoadingIcon />}
    </Grid>
  );
}
